import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Copy, PlayCircle, Video, Inbox, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Navbar } from "@/components/Navbar";
import { getVault, removeFromVault } from "@/lib/vault";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Vault() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [target, setTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setItems(getVault());
  }, []);

  const copy = async (code) => {
    await navigator.clipboard.writeText(code);
    toast.success("Code copied");
  };

  const confirmDelete = async () => {
    if (!target) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/messages/${encodeURIComponent(target.code)}`);
      removeFromVault(target.code);
      setItems(getVault());
      toast.success("Message deleted and its code retired");
      setTarget(null);
    } catch (e) {
      if (e?.response?.status === 404) {
        // Already gone on the server — clean up locally anyway
        removeFromVault(target.code);
        setItems(getVault());
        toast.success("Message removed from your vault");
        setTarget(null);
      } else {
        toast.error("Couldn't delete the message. Please try again.");
      }
    } finally {
      setDeleting(false);
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-obsidian bg-grain">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-32 pb-20">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl font-medium text-gold-light">My vault</h1>
            <p className="text-slate-400 mt-2">Messages sealed on this device, with their unlock codes.</p>
          </div>
          <Button data-testid="vault-record-button" onClick={() => navigate("/record")} className="bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full px-6 self-start">
            <Video className="w-4 h-4 mr-1" /> Record new
          </Button>
        </div>

        {items.length === 0 ? (
          <div className="glass-card rounded-2xl p-14 text-center" data-testid="vault-empty">
            <Inbox className="w-10 h-10 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-300 font-serif text-xl mb-2">No messages yet</p>
            <p className="text-slate-500 text-sm mb-6">Record your first 30-second message to keep it safe here.</p>
            <Button onClick={() => navigate("/record")} className="bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full px-6">
              Record a message
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((m, i) => (
              <div key={m.code} data-testid={`vault-item-${i}`} className="glass-card rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div className="min-w-0">
                  <h3 className="font-serif text-xl text-gold-light truncate">{m.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">
                    {m.recipient_name ? `For ${m.recipient_name} · ` : ""}{fmt(m.created_at)}
                  </p>
                  <p className="font-mono text-gold text-lg tracking-widest mt-3">{m.code}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button data-testid={`vault-copy-${i}`} onClick={() => copy(m.code)} variant="outline" className="rounded-full border-gold/30 text-gold-light bg-transparent hover:bg-gold/10">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button data-testid={`vault-play-${i}`} onClick={() => navigate(`/unlock/${m.code}`)} className="rounded-full bg-gold/15 text-gold hover:bg-gold/25 border border-gold/30">
                    <PlayCircle className="w-4 h-4 mr-1" /> Watch
                  </Button>
                  <Button data-testid={`vault-delete-${i}`} onClick={() => setTarget(m)} variant="outline" className="rounded-full border-destructive/40 text-destructive bg-transparent hover:bg-destructive/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent data-testid="delete-dialog" className="glass-card border-gold/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-gold-light text-2xl">Delete this message?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              "{target?.title}" will be permanently sealed away and its code
              <span className="font-mono text-gold"> {target?.code} </span>
              will no longer unlock anything. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" className="rounded-full bg-transparent border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white">
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-button"
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="rounded-full bg-destructive hover:bg-destructive/90 text-white"
            >
              {deleting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Deleting…</> : <>Delete & retire code</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
